import { Search } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { ChangeEvent, HTMLAttributes, forwardRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { TableComponents, TableVirtuoso } from 'react-virtuoso';
import {
  itemTagsClient,
  ItemTagAssignableItem,
  ItemTagCatalogueOption,
  ItemTagCategoryOption,
  ItemTagDto,
  ItemTagInstanceOption,
  ItemTagMarketOption,
  ItemTagUpsertDto,
} from '../../../../app/services/api/itemTags';
import { client } from '../../../../app/services/api/orderManagementClient';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';
import PageArea from '../../../../components/shared/PageArea';
import PageTitle from '../../../../components/shared/PageTitle';
import BackButton from '../../../../components/shared/actions/BackButton';
import { UserContext } from '../../../../components/shared/useUser';
import { Section } from '../../../../components/ui/Section';
import {
  DEFAULT_ITEM_TAG_COLORS,
  findMaterialIconOption,
  isValidHexColor,
  MATERIAL_ICON_OPTIONS,
  normalizeHexColor,
} from './itemTagUtils';
import { ItemTagChip } from './ItemTagChip';

type FeedbackState = {
  message: string;
  severity: 'success' | 'error';
} | null;

enum AssignmentView {
  All = 'all',
  Assigned = 'assigned',
  Unassigned = 'unassigned',
}

const AssignmentTableScroller = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(function AssignmentTableScroller(props, ref) {
  const { children, ...rest } = props;

  return (
    <TableContainer
      component='div'
      ref={ref}
      {...rest}
      sx={{
        height: '100%',
        overflowX: 'auto',
      }}>
      {children}
    </TableContainer>
  );
});

AssignmentTableScroller.displayName = 'AssignmentTableScroller';

const AssignmentTableBody = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>((props, ref) => (
  <TableBody {...props}
    ref={ref} />
));

AssignmentTableBody.displayName = 'AssignmentTableBody';

const assignmentTableComponents = {
  Scroller: AssignmentTableScroller,
  Table: (props: HTMLAttributes<HTMLTableElement>) => (
    <Table {...props}
      size='small'
      aria-label='item tag assignment table'
      sx={{ minWidth: 920, tableLayout: 'auto', borderCollapse: 'separate' }} />
  ),
  TableRow: (props: HTMLAttributes<HTMLTableRowElement>) => (
    <TableRow {...props}
      hover
      sx={{ height: 56 }} />
  ),
  TableBody: AssignmentTableBody,
} as unknown as TableComponents<ItemTagAssignableItem>;

const ITEMS_PAGE_SIZE = 50;

const emptyFormState: ItemTagUpsertDto = {
  name: '',
  textColor: DEFAULT_ITEM_TAG_COLORS.textColor,
  backgroundColor: DEFAULT_ITEM_TAG_COLORS.backgroundColor,
  iconKey: 'SpaOutlined',
  isActive: true,
  companyId: 0,
  customerTypeId: 0,
};

const toErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

const uniqueNumberList = (values: number[]) => Array.from(new Set(values));

const areNumberSetsEqual = (left: number[], right: number[]) => {
  if (left.length !== right.length) {
    return false;
  }

  const leftSet = new Set(left);
  return right.every((value) => leftSet.has(value));
};

const resolveDefaultInstanceId = (
  availableInstances: ItemTagInstanceOption[],
  preferredCompanyId?: number | null,
) => {
  if (availableInstances.length === 0) {
    return 0;
  }

  if (preferredCompanyId && availableInstances.some((instance) => instance.id === preferredCompanyId)) {
    return preferredCompanyId;
  }

  return 0;
};

const resolveDefaultMarketId = (
  availableMarkets: ItemTagMarketOption[],
  preferredFranchiseName?: string,
) => {
  if (availableMarkets.length === 0) {
    return 0;
  }

  const normalizedFranchiseName = preferredFranchiseName?.trim().toLowerCase();
  if (!normalizedFranchiseName) {
    return 0;
  }

  const matchingMarket = availableMarkets.find(
    (market) => market.name.trim().toLowerCase() === normalizedFranchiseName,
  );

  return matchingMarket?.id ?? 0;
};

const ItemTagEditorPage = () => {
  const { t } = useTranslation('itemTags');
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { itemTagId } = useParams<{ itemTagId: string }>();
  const { hasPermissionTo, selectedFranchise, selectedStore } = useContext(UserContext);

  // Stable navigate ref — prevents the loadItemTag effect from re-running when React
  // Router rebuilds the navigate function reference after Clerk token refreshes.
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  });

  const parsedItemTagId = itemTagId ? Number(itemTagId) : undefined;
  const isEdit = parsedItemTagId !== undefined && !Number.isNaN(parsedItemTagId);

  const canCreateItemTags = hasPermissionTo(['ItemTagCreate', 'ConfigurationAdmin']);
  const canUpdateItemTags = hasPermissionTo(['ItemTagUpdate', 'ConfigurationAdmin']);
  const canAssignItemTags = hasPermissionTo(['ItemTagAssign', 'ConfigurationAdmin']);
  const canEditBasicInfo = isEdit ? canUpdateItemTags : canCreateItemTags;

  const [formData, setFormData] = useState<ItemTagUpsertDto>(emptyFormState);
  const [loadedTag, setLoadedTag] = useState<ItemTagDto | null>(null);
  const [instances, setInstances] = useState<ItemTagInstanceOption[]>([]);
  const [markets, setMarkets] = useState<ItemTagMarketOption[]>([]);
  const [categories, setCategories] = useState<ItemTagCategoryOption[]>([]);
  const [catalogues, setCatalogues] = useState<ItemTagCatalogueOption[]>([]);
  const [items, setItems] = useState<ItemTagAssignableItem[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCatalogue, setSelectedCatalogue] = useState('');
  const [assignmentView, setAssignmentView] = useState<AssignmentView>(AssignmentView.All);
  const [persistedAssignedItemIds, setPersistedAssignedItemIds] = useState<number[]>([]);
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
  const [loggedInCompanyId, setLoggedInCompanyId] = useState<number | null>(null);
  const [isResolvingLoggedInCompanyId, setIsResolvingLoggedInCompanyId] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  // First-page load spinner (replaces isItemsLoading); separate flag for append loads
  const [isLoadingFirstPage, setIsLoadingFirstPage] = useState(false);
  const [isLoadingMoreItems, setIsLoadingMoreItems] = useState(false);
  const [loadedPageCount, setLoadedPageCount] = useState(0);
  const [totalItemPages, setTotalItemPages] = useState(0);
  const [totalItemCount, setTotalItemCount] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState<FeedbackState>(null);

  const assignmentSectionRef = useRef<HTMLDivElement | null>(null);
  // Refs to ensure auto-selection runs at most once — prevents context re-renders
  // (e.g. Clerk token refresh) from resetting user's manual selections.
  const instanceAutoSelectedRef = useRef(false);
  const marketAutoSelectedRef = useRef(false);
  // Prevents the async loadAssignedItemIds effect from overriding the user's
  // unchecked selections after a second invocation (e.g. permission re-evaluation).
  const assignmentInitializedRef = useRef(false);

  // Ref guard prevents concurrent page requests
  const isLoadingMoreRef = useRef(false);

  const hasMarketContext = formData.companyId > 0 && formData.customerTypeId > 0;
  const hasMoreItems = loadedPageCount < totalItemPages;
  const isScopeLocked = Boolean(isEdit && loadedTag && loadedTag.assignedItemCount > 0);

  const selectedItemIdSet = useMemo(() => new Set(selectedItemIds), [selectedItemIds]);
  const persistedAssignedItemIdSet = useMemo(() => new Set(persistedAssignedItemIds), [persistedAssignedItemIds]);
  const selectedIconOption = useMemo(
    () => findMaterialIconOption(formData.iconKey) ?? findMaterialIconOption('LocalOfferOutlined') ?? MATERIAL_ICON_OPTIONS[0],
    [formData.iconKey],
  );

  const displayedItems = useMemo(() => items.filter((item) => {
    const isSelected = selectedItemIdSet.has(item.contentId);

    if (assignmentView === AssignmentView.Assigned) {
      return isSelected;
    }

    if (assignmentView === AssignmentView.Unassigned) {
      return !isSelected;
    }

    return true;
  }), [assignmentView, items, selectedItemIdSet]);

  const allDisplayedSelected = displayedItems.length > 0
    && displayedItems.every((item) => selectedItemIdSet.has(item.contentId));
  const hasDisplayedSelection = displayedItems.some((item) => selectedItemIdSet.has(item.contentId));

  const hasAssignmentChanges = useMemo(
    () => !areNumberSetsEqual(selectedItemIds, persistedAssignedItemIds),
    [persistedAssignedItemIds, selectedItemIds],
  );

  useEffect(() => {
    let isActive = true;

    const storeNumber = selectedStore?.storeNumber?.trim();
    if (!storeNumber) {
      setLoggedInCompanyId(null);
      setIsResolvingLoggedInCompanyId(false);
      return () => {
        isActive = false;
      };
    }

    setIsResolvingLoggedInCompanyId(true);

    const loadLoggedInCompanyId = async () => {
      try {
        const result = await client.getCompanyIdByStoreNumber(storeNumber);

        if (isActive) {
          setLoggedInCompanyId(result.success ? result.data : null);
        }
      } catch {
        if (isActive) {
          setLoggedInCompanyId(null);
        }
      } finally {
        if (isActive) {
          setIsResolvingLoggedInCompanyId(false);
        }
      }
    };

    loadLoggedInCompanyId();

    return () => {
      isActive = false;
    };
  }, [selectedStore?.storeNumber]);

  useEffect(() => {
    let isActive = true;

    const loadInstances = async () => {
      try {
        const result = await client.getInstances({
          name: '',
          sort: 'name',
          direction: 'asc',
          pageNumber: 0,
          pageSize: 200,
        });
        const nextInstances: ItemTagInstanceOption[] = (result.data?.items ?? []).map((instance) => ({
          id: instance.id,
          name: instance.name,
        }));
        if (isActive) {
          setInstances(nextInstances);
        }
      } catch (error) {
        if (isActive) {
          setFeedback({
            message: toErrorMessage(error, t('loadErrorMessage')),
            severity: 'error',
          });
        }
      }
    };

    loadInstances();

    return () => {
      isActive = false;
    };
  }, [t]);

  useEffect(() => {
    let isActive = true;

    const loadItemTag = async () => {
      if (!isEdit || !parsedItemTagId) {
        // Create mode: do NOT reset formData here — it is already initialised to
        // emptyFormState by useState, and resetting it here would wipe the
        // instance/franchise auto-selection every time this effect re-fires.
        setLoadedTag(null);
        return;
      }

      setIsPageLoading(true);

      try {
        const nextTag = await itemTagsClient.getItemTag(parsedItemTagId);
        if (!nextTag) {
          navigateRef.current('/item-tags');
          return;
        }

        if (!isActive) {
          return;
        }

        setLoadedTag(nextTag);
        setFormData({
          name: nextTag.name,
          textColor: nextTag.textColor,
          backgroundColor: nextTag.backgroundColor,
          iconKey: nextTag.iconKey,
          isActive: nextTag.isActive,
          companyId: nextTag.companyId,
          customerTypeId: nextTag.customerTypeId,
        });
      } catch (error) {
        if (isActive) {
          setFeedback({
            message: toErrorMessage(error, t('loadErrorMessage')),
            severity: 'error',
          });
          navigateRef.current('/item-tags');
        }
      } finally {
        if (isActive) {
          setIsPageLoading(false);
        }
      }
    };

    loadItemTag();

    return () => {
      isActive = false;
    };
  }, [isEdit, parsedItemTagId, t]);

  useEffect(() => {
    let isActive = true;

    const loadMarkets = async () => {
      if (!formData.companyId) {
        setMarkets([]);
        return;
      }

      try {
        const result = await client.getMarkets({
          companyId: formData.companyId.toString(),
          name: '',
          sort: 'name',
          direction: 'asc',
          pageNumber: 0,
          pageSize: 500,
        });
        const nextMarkets: ItemTagMarketOption[] = (result.data?.items ?? []).map((market) => ({
          id: market.customerTypeId,
          companyId: market.companyId,
          name: market.name,
          instanceName: market.instanceName,
        }));
        if (isActive) {
          setMarkets(nextMarkets);
        }
      } catch (error) {
        if (isActive) {
          setFeedback({
            message: toErrorMessage(error, t('loadErrorMessage')),
            severity: 'error',
          });
        }
      }
    };

    loadMarkets();

    return () => {
      isActive = false;
    };
  }, [formData.companyId, t]);

  useEffect(() => {
    // Only auto-select for create mode, and only once per page load.
    if (isEdit || instanceAutoSelectedRef.current || instances.length === 0 || isResolvingLoggedInCompanyId) {
      return;
    }

    const defaultInstanceId = resolveDefaultInstanceId(
      instances,
      loggedInCompanyId,
    );

    if (!defaultInstanceId) {
      return;
    }

    instanceAutoSelectedRef.current = true;
    setFormData((current) => {
      if (current.companyId) {
        return current; // user already made a manual selection
      }

      return { ...current, companyId: defaultInstanceId };
    });
  }, [
    instances,
    isEdit,
    isResolvingLoggedInCompanyId,
    loggedInCompanyId,
  ]);

  useEffect(() => {
    // Only auto-select for create mode, and once per instance selection.
    // marketAutoSelectedRef is reset whenever the user changes the instance.
    if (isEdit || marketAutoSelectedRef.current || markets.length === 0) {
      return;
    }

    const defaultMarketId = resolveDefaultMarketId(markets, selectedFranchise?.franchiseName);
    if (!defaultMarketId) {
      return;
    }

    marketAutoSelectedRef.current = true;
    setFormData((current) => {
      if (current.customerTypeId) {
        return current; // user already made a manual selection
      }

      return { ...current, customerTypeId: defaultMarketId };
    });
  }, [markets, isEdit, selectedFranchise?.franchiseName]);

  // Load catalogues when market context changes
  useEffect(() => {
    let isActive = true;

    const loadCatalogues = async () => {
      if (formData.companyId <= 0 || formData.customerTypeId <= 0) {
        setCatalogues([]);
        setSelectedCatalogue('');
        return;
      }

      try {
        const result = await client.getMarketCatalogues({
          companyId: formData.companyId.toString(),
          customerTypeId: formData.customerTypeId.toString(),
          searchTerm: '',
          sort: 'name',
          direction: 'asc',
          pageNumber: 0,
          pageSize: 500,
        });
        const nextCatalogues: ItemTagCatalogueOption[] = (result.data?.items ?? []).map((catalogue) => ({
          id: catalogue.customerGroupId,
          name: catalogue.name,
        }));

        if (isActive) {
          setCatalogues(nextCatalogues);
        }
      } catch (error) {
        if (isActive) {
          setFeedback({
            message: toErrorMessage(error, t('loadErrorMessage')),
            severity: 'error',
          });
        }
      }
    };

    loadCatalogues();

    return () => {
      isActive = false;
    };
  }, [formData.companyId, formData.customerTypeId, t]);

  // Load catalogue-specific categories whenever the selected catalogue changes
  useEffect(() => {
    let isActive = true;

    const loadCategoriesForCatalogue = async () => {
      if (!selectedCatalogue) {
        setCategories([]);
        setSelectedCategory('');
        return;
      }

      try {
        const result = await client.getCategoriesByCatalogue(
          selectedStore?.storeNumber ?? '',
          selectedCatalogue,
        );
        const nextCategories: ItemTagCategoryOption[] = (result.data ?? []).map((category) => ({
          id: category.id,
          name: category.name,
        }));

        if (isActive) {
          setCategories(nextCategories);
        }
      } catch (error) {
        if (isActive) {
          setFeedback({
            message: toErrorMessage(error, t('loadErrorMessage')),
            severity: 'error',
          });
        }
      }
    };

    loadCategoriesForCatalogue();

    return () => {
      isActive = false;
    };
  }, [selectedCatalogue, formData.companyId, formData.customerTypeId, selectedStore?.storeNumber, t]);

  useEffect(() => {
    if (parsedItemTagId) {
      return;
    }

    setPersistedAssignedItemIds([]);
    setSelectedItemIds([]);
  }, [formData.companyId, formData.customerTypeId, parsedItemTagId]);

  useEffect(() => {
    let isActive = true;

    const loadAssignedItemIds = async () => {
      if (!parsedItemTagId || !canAssignItemTags) {
        setPersistedAssignedItemIds([]);
        return;
      }

      try {
        const nextAssignedItemIds = await itemTagsClient.getAssignedItemIds(parsedItemTagId);

        if (!isActive) {
          return;
        }

        setPersistedAssignedItemIds(nextAssignedItemIds);
        // Only initialise selectedItemIds once — do not overwrite changes the user
        // has already made (e.g. unchecking items before this async call returns).
        if (!assignmentInitializedRef.current) {
          setSelectedItemIds(nextAssignedItemIds);
          assignmentInitializedRef.current = true;
        }
      } catch (error) {
        if (isActive) {
          setFeedback({
            message: toErrorMessage(error, t('assignmentLoadErrorMessage')),
            severity: 'error',
          });
        }
      }
    };

    loadAssignedItemIds();

    return () => {
      isActive = false;
    };
  }, [canAssignItemTags, parsedItemTagId, t]);

  // Load page 0 whenever any filter changes — resets the entire item list
  useEffect(() => {
    let isActive = true;

    const loadFirstPage = async () => {
      if (formData.companyId <= 0 || formData.customerTypeId <= 0 || !canAssignItemTags || !selectedCatalogue) {
        setItems([]);
        setLoadedPageCount(0);
        setTotalItemPages(0);
        setTotalItemCount(0);
        isLoadingMoreRef.current = false;
        return;
      }

      setIsLoadingFirstPage(true);
      setItems([]);
      setLoadedPageCount(0);
      setTotalItemPages(0);
      setTotalItemCount(0);
      isLoadingMoreRef.current = false;

      try {
        const page = await itemTagsClient.getAssignableItems({
          itemTagId: parsedItemTagId,
          companyId: formData.companyId,
          customerTypeId: formData.customerTypeId,
          storeNumber: selectedStore?.storeNumber,
        }, {
          searchText: searchText || undefined,
          categoryId: selectedCategory ? Number(selectedCategory) : undefined,
          catalogueId: Number(selectedCatalogue),
          pageNumber: 0,
          pageSize: ITEMS_PAGE_SIZE,
        });

        if (isActive) {
          setItems(page.items);
          setTotalItemPages(page.totalPages);
          setTotalItemCount(page.totalCount);
          setLoadedPageCount(1);
        }
      } catch (error) {
        if (isActive) {
          setFeedback({
            message: toErrorMessage(error, t('assignmentLoadErrorMessage')),
            severity: 'error',
          });
        }
      } finally {
        if (isActive) {
          setIsLoadingFirstPage(false);
        }
      }
    };

    loadFirstPage();

    return () => {
      isActive = false;
    };
  }, [
    canAssignItemTags,
    formData.companyId,
    formData.customerTypeId,
    parsedItemTagId,
    searchText,
    selectedCategory,
    selectedCatalogue,
    selectedStore?.storeNumber,
    t,
  ]);

  // Append next page when the user scrolls to the bottom of the virtualized list
  const loadMoreItems = useCallback(async () => {
    if (
      isLoadingMoreRef.current ||
      isLoadingFirstPage ||
      !hasMoreItems ||
      formData.companyId <= 0 ||
      formData.customerTypeId <= 0 ||
      !selectedCatalogue
    ) {
      return;
    }

    isLoadingMoreRef.current = true;
    setIsLoadingMoreItems(true);

    try {
      const page = await itemTagsClient.getAssignableItems({
        itemTagId: parsedItemTagId,
        companyId: formData.companyId,
        customerTypeId: formData.customerTypeId,
        storeNumber: selectedStore?.storeNumber,
      }, {
        searchText: searchText || undefined,
        categoryId: selectedCategory ? Number(selectedCategory) : undefined,
        catalogueId: Number(selectedCatalogue),
        pageNumber: loadedPageCount,
        pageSize: ITEMS_PAGE_SIZE,
      });

      setItems((prev) => [...prev, ...page.items]);
      setTotalItemPages(page.totalPages);
      setLoadedPageCount((prev) => prev + 1);
    } catch (error) {
      setFeedback({
        message: toErrorMessage(error, t('assignmentLoadErrorMessage')),
        severity: 'error',
      });
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMoreItems(false);
    }
  }, [
    hasMoreItems,
    isLoadingFirstPage,
    loadedPageCount,
    parsedItemTagId,
    formData.companyId,
    formData.customerTypeId,
    selectedStore?.storeNumber,
    searchText,
    selectedCategory,
    selectedCatalogue,
    t,
  ]);

  useEffect(() => {
    if (location.hash === '#assignment' && assignmentSectionRef.current && !isPageLoading) {
      assignmentSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isPageLoading, location.hash]);

  const setFieldValue = <TKey extends keyof ItemTagUpsertDto>(field: TKey, value: ItemTagUpsertDto[TKey]) => {
    setFormData((current) => ({
      ...current,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((current) => ({
        ...current,
        [field]: '',
      }));
    }
  };

  const validateForm = () => {
    if (!canEditBasicInfo) {
      return true;
    }

    const nextErrors: Record<string, string> = {};
    const trimmedName = formData.name.trim();

    if (!trimmedName) {
      nextErrors.name = t('validationTagName');
    } else if (trimmedName.length > 50) {
      nextErrors.name = t('validationTagNameLength');
    }

    if (!formData.companyId) {
      nextErrors.companyId = t('validationInstance');
    }

    if (!formData.customerTypeId) {
      nextErrors.customerTypeId = t('validationMarket');
    }

    if (!isValidHexColor(formData.textColor)) {
      nextErrors.textColor = t('validationTextColor');
    }

    if (!isValidHexColor(formData.backgroundColor)) {
      nextErrors.backgroundColor = t('validationBackgroundColor');
    }

    if (!formData.iconKey) {
      nextErrors.iconKey = t('validationIcon');
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleInstanceChange = (event: SelectChangeEvent<string>) => {
    if (isScopeLocked) {
      return;
    }

    const companyId = Number(event.target.value);

    setFormData((current) => ({
      ...current,
      companyId,
      customerTypeId: 0,
    }));
    setSelectedCategory('');
    setSelectedCatalogue('');
    setSearchInput('');
    setSearchText('');
    setErrors((current) => ({
      ...current,
      companyId: '',
      customerTypeId: '',
    }));
  };

  const handleMarketChange = (event: SelectChangeEvent<string>) => {
    if (isScopeLocked) {
      return;
    }

    const customerTypeId = Number(event.target.value);
    const selectedMarket = markets.find((market) => market.id === customerTypeId);

    setFormData((current) => ({
      ...current,
      companyId: selectedMarket?.companyId ?? current.companyId,
      customerTypeId,
    }));
    setSelectedCategory('');
    setSelectedCatalogue('');
    setSearchInput('');
    setSearchText('');
    setErrors((current) => ({
      ...current,
      companyId: '',
      customerTypeId: '',
    }));
  };

  const handleColorInput = (field: 'textColor' | 'backgroundColor', value: string) => {
    setFieldValue(field, normalizeHexColor(value));
  };

  const handleSelectAll = (checked: boolean) => {
    const displayedItemIds = displayedItems.map((item) => item.contentId);

    if (!checked) {
      setSelectedItemIds((current) => current.filter((id) => !displayedItemIds.includes(id)));
      return;
    }

    setSelectedItemIds((current) => uniqueNumberList([...current, ...displayedItemIds]));
  };

  const handleSelectItem = (item: ItemTagAssignableItem) => {
    setSelectedItemIds((current) => (
      current.includes(item.contentId)
        ? current.filter((value) => value !== item.contentId)
        : [...current, item.contentId]
    ));
  };

  const handleSubmit = async () => {
    if (!canEditBasicInfo && (!parsedItemTagId || !hasAssignmentChanges)) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      let effectiveItemTagId = parsedItemTagId;

      if (canEditBasicInfo) {
        if (parsedItemTagId) {
          await itemTagsClient.updateItemTag(parsedItemTagId, formData);
        } else {
          const createdItemTag = await itemTagsClient.createItemTag(formData);
          effectiveItemTagId = createdItemTag.id;
        }
      }

      if (canAssignItemTags && effectiveItemTagId && hasAssignmentChanges) {
        await itemTagsClient.assignItems(effectiveItemTagId, selectedItemIds);
      }

      let successMessage = t('updateSuccessMessage');

      if (!canEditBasicInfo) {
        successMessage = t('assignSuccessMessage');
      } else if (!parsedItemTagId && canAssignItemTags && selectedItemIds.length > 0) {
        successMessage = t('createAndAssignSuccessMessage');
      } else if (!parsedItemTagId) {
        successMessage = t('createSuccessMessage');
      } else if (canAssignItemTags && hasAssignmentChanges) {
        successMessage = t('saveAndAssignSuccessMessage');
      }

      setFeedback({ message: successMessage, severity: 'success' });
      setTimeout(() => navigate('/item-tags'), 500);
    } catch (error) {
      setFeedback({
        message: toErrorMessage(error, t('saveErrorMessage')),
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleExitAndSave = () => {
    if (isSaving) {
      return;
    }

    handleSubmit();
  };

  const primaryButtonLabel = (() => {
    if (isSaving) {
      return canEditBasicInfo ? t('saving') : t('assigning');
    }

    if (!canEditBasicInfo) {
      return t('saveAssignments');
    }

    if (!parsedItemTagId && canAssignItemTags && selectedItemIds.length > 0) {
      return t('createTagAndAssign');
    }

    return parsedItemTagId ? t('saveChanges') : t('save');
  })();

  const showAssignmentEmptyState = !hasMarketContext || !canAssignItemTags;
  const assignmentHeaderLabel = assignmentView === AssignmentView.Assigned
    ? t('assignedItemsOnly')
    : assignmentView === AssignmentView.Unassigned
      ? t('unassignedItemsOnly')
      : t('allAssignmentStates');
  const sectionGridSx = {
    px: '10px',
    '& > .MuiGrid-item:first-of-type': {
      pt: '10px',
    },
    '& > .MuiGrid-item:last-of-type': {
      pb: '10px',
    },
  };

  const getAssignmentStatus = (item: ItemTagAssignableItem) => {
    const wasAssigned = persistedAssignedItemIdSet.has(item.contentId);
    const isSelected = selectedItemIdSet.has(item.contentId);

    if (wasAssigned && isSelected) {
      return { label: t('assignmentStatusAssigned'), color: 'success' as const };
    }

    if (!wasAssigned && isSelected) {
      return { label: t('assignmentStatusToBeAssigned'), color: 'info' as const };
    }

    if (wasAssigned && !isSelected) {
      return { label: t('assignmentStatusToBeUnassigned'), color: 'warning' as const };
    }

    return { label: t('assignmentStatusAvailable'), color: 'default' as const };
  };

  if (isPageLoading) {
    return (
      <PageArea>
        <PageTitle>{isEdit ? t('editTag') : t('createTag')}</PageTitle>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <BackButton
        handleClick={handleExitAndSave}
        title={t('exitAndSave')}
        isLink={false}
        styles={{
          marginBottom: '20px',
          opacity: isSaving ? 0.6 : 1,
          pointerEvents: isSaving ? 'none' : 'auto',
        }}
      />

      <Grid
        container
        spacing={3}
        alignItems='flex-end'
        sx={{ mb: 3 }}
      >
        <Grid
          item
          xs={12}
          md={12}
        >
          <PageTitle subTitle={canEditBasicInfo ? t('editorSubtitle') : t('assignOnlySubtitle')}>
            {isEdit ? t('editTag') : t('createTag')}
          </PageTitle>
        </Grid>
      </Grid>

      <Stack spacing={3}>
        <Grid
          container
          spacing={3}
          alignItems='stretch'
        >
          <Grid
            item
            xs={12}
            xl={4}
          >
            <Section style={{ height: '100%' }}>
              <Box sx={{ p: 3 }}>
                <Grid
                  container
                  spacing={3}
                  sx={sectionGridSx}
                >
                  <Grid
                    item
                    xs={12}
                  >
                    <Box>
                      <Typography
                        variant='textLG'
                        fontWeight={600}
                        component='h2'
                      >
                        {t('basicInformation')}
                      </Typography>
                      <Typography
                        variant='textSM'
                        color='text.secondary'
                      >
                        {t('basicInformationSubtitle')}
                      </Typography>
                    </Box>
                  </Grid>

                  {!canEditBasicInfo && (
                    <Grid
                      item
                      xs={12}
                    >
                      <Alert severity='info'>
                        {t('basicInfoReadOnly')}
                      </Alert>
                    </Grid>
                  )}

                  <Grid
                    item
                    xs={12}
                  >
                    <TextField
                      fullWidth
                      label={t('tagName')}
                      placeholder={t('tagNamePlaceholder')}
                      value={formData.name}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => setFieldValue('name', event.target.value)}
                      error={Boolean(errors.name)}
                      helperText={errors.name || undefined}
                      disabled={!canEditBasicInfo}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                  >
                    <FormControl
                      fullWidth
                      error={Boolean(errors.companyId)}
                    >
                      <InputLabel shrink>{t('instance')}</InputLabel>
                      <Select
                        value={formData.companyId ? formData.companyId.toString() : ''}
                        label={t('instance')}
                        onChange={handleInstanceChange}
                        displayEmpty
                        notched
                        disabled={!canEditBasicInfo || isScopeLocked}
                      >
                        <MenuItem value=''>{t('selectInstance')}</MenuItem>
                        {instances.map((instance) => (
                          <MenuItem key={instance.id}
                            value={instance.id.toString()}>
                            {instance.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.companyId && (
                        <Typography
                          variant='textXS'
                          color='error'
                          sx={{ mt: 1 }}
                        >
                          {errors.companyId}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                  >
                    <FormControl
                      fullWidth
                      error={Boolean(errors.customerTypeId)}
                    >
                      <InputLabel shrink>{t('market')}</InputLabel>
                      <Select
                        value={formData.customerTypeId ? formData.customerTypeId.toString() : ''}
                        label={t('market')}
                        onChange={handleMarketChange}
                        displayEmpty
                        notched
                        disabled={!canEditBasicInfo || isScopeLocked}
                      >
                        <MenuItem value=''>{t('selectMarket')}</MenuItem>
                        {markets.map((market) => (
                          <MenuItem key={market.id}
                            value={market.id.toString()}>
                            {market.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.customerTypeId && (
                        <Typography
                          variant='textXS'
                          color='error'
                          sx={{ mt: 1 }}
                        >
                          {errors.customerTypeId}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        mb: '20px',
                      }}
                    >
                      <Typography variant='textSM'>{t('preview')}</Typography>
                      <ItemTagChip
                        name={formData.name}
                        textColor={formData.textColor}
                        backgroundColor={formData.backgroundColor}
                        iconKey={formData.iconKey}
                        isActive={formData.isActive}
                        placeholderLabel={t('previewFallbackName')}
                      />
                    </Box>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                      }}
                    >
                      <TextField
                        fullWidth
                        label={t('textColor')}
                        placeholder={t('hexPlaceholder')}
                        value={formData.textColor}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => handleColorInput('textColor', event.target.value)}
                        error={Boolean(errors.textColor)}
                        helperText={errors.textColor || t('hexValueHint')}
                        inputProps={{ maxLength: 7 }}
                        disabled={!canEditBasicInfo}
                      />
                      <Box
                        component='input'
                        type='color'
                        value={isValidHexColor(formData.textColor) ? formData.textColor : DEFAULT_ITEM_TAG_COLORS.textColor}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => handleColorInput('textColor', event.target.value)}
                        disabled={!canEditBasicInfo}
                        sx={{
                          mt: 0.5,
                          width: 44,
                          height: 44,
                          p: 0,
                          flexShrink: 0,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1.5,
                          backgroundColor: 'transparent',
                          cursor: canEditBasicInfo ? 'pointer' : 'default',
                        }}
                      />
                    </Box>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                      }}
                    >
                      <TextField
                        fullWidth
                        label={t('backgroundColor')}
                        placeholder={t('hexPlaceholder')}
                        value={formData.backgroundColor}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => handleColorInput('backgroundColor', event.target.value)}
                        error={Boolean(errors.backgroundColor)}
                        helperText={errors.backgroundColor || t('hexValueHint')}
                        inputProps={{ maxLength: 7 }}
                        disabled={!canEditBasicInfo}
                      />
                      <Box
                        component='input'
                        type='color'
                        value={isValidHexColor(formData.backgroundColor) ? formData.backgroundColor : DEFAULT_ITEM_TAG_COLORS.backgroundColor}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => handleColorInput('backgroundColor', event.target.value)}
                        disabled={!canEditBasicInfo}
                        sx={{
                          mt: 0.5,
                          width: 44,
                          height: 44,
                          p: 0,
                          flexShrink: 0,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1.5,
                          backgroundColor: 'transparent',
                          cursor: canEditBasicInfo ? 'pointer' : 'default',
                        }}
                      />
                    </Box>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                  >
                    <Autocomplete
                      autoHighlight
                      openOnFocus
                      disablePortal
                      options={MATERIAL_ICON_OPTIONS}
                      value={selectedIconOption}
                      disabled={!canEditBasicInfo}
                      noOptionsText={t('noIconsFound')}
                      ListboxProps={{
                        style: {
                          display: 'grid',
                          gridTemplateColumns: 'repeat(8, minmax(68px, 1fr))',
                          gap: '10px',
                          padding: '12px',
                          margin: 0,
                          maxHeight: '320px',
                          alignItems: 'stretch',
                        },
                      }}
                      isOptionEqualToValue={(option, value) => option.key === value.key}
                      getOptionLabel={(option) => option.label}
                      filterOptions={(options, state) => {
                        const normalizedQuery = state.inputValue.trim().toLowerCase();

                        if (!normalizedQuery) {
                          return options.slice(0, 120);
                        }

                        return options
                          .filter((option) => option.searchText.includes(normalizedQuery))
                          .slice(0, 120);
                      }}
                      onChange={(_, value) => {
                        if (value) {
                          setFieldValue('iconKey', value.key);
                        }
                      }}
                      renderOption={(props, option) => {
                        const Icon = option.Icon;
                        const isSelected = selectedIconOption?.key === option.key;

                        return (
                          <Box component='li'
                            {...props}
                            sx={{
                              listStyle: 'none',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 0.5,
                              minHeight: 72,
                              px: 0.5,
                              py: 0.75,
                              borderRadius: 2,
                              border: isSelected ? '1px solid rgba(33, 150, 243, 0.45)' : '1px solid transparent',
                              backgroundColor: isSelected ? 'rgba(33, 150, 243, 0.1)' : 'transparent',
                              color: theme.palette.text.primary,
                              transition: 'background-color 120ms ease, border-color 120ms ease',
                              '&:hover': {
                                backgroundColor: theme.palette.custom.gray[100],
                              },
                            }}>
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                color: 'inherit',
                              }}>
                              <Icon fontSize='small' />
                            </Box>
                            <Typography
                              variant='textXS'
                              sx={{
                                color: 'inherit',
                                textAlign: 'center',
                                lineHeight: 1.2,
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}>
                              {option.label}
                            </Typography>
                          </Box>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          label={t('searchIcons')}
                          placeholder={t('iconSearchPlaceholder')}
                          error={Boolean(errors.iconKey)}
                          helperText={errors.iconKey || t('iconHelpText')}
                        />
                      )}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      }}
                    >
                      <FormControlLabel
                        control={(
                          <Switch
                            checked={formData.isActive}
                            onChange={(event) => setFieldValue('isActive', event.target.checked)}
                            disabled={!canEditBasicInfo}
                          />
                        )}
                        label={t('activeLabel')}
                      />
                      <Typography
                        variant='textXS'
                        color='text.secondary'
                      >
                        {formData.isActive ? t('statusHelpActive') : t('statusHelpInactive')}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Section>
          </Grid>

          <Grid
            item
            xs={12}
            xl={8}
          >
            <Box
              ref={assignmentSectionRef}
              sx={{ height: '100%' }}
            >
              <Section style={{ height: '100%' }}>
                <Box sx={{ p: 3 }}>
                  <Grid
                    container
                    spacing={3}
                    sx={sectionGridSx}
                  >
                    <Grid
                      item
                      xs={12}
                    >
                      <Box>
                        <Typography
                          variant='textLG'
                          fontWeight={600}
                          component='h2'
                        >
                          {t('assignmentSectionTitle')}
                        </Typography>
                        <Typography
                          variant='textSM'
                          color='text.secondary'
                        >
                          {t('assignmentSectionSubtitle')}
                        </Typography>
                      </Box>
                    </Grid>

                    {!hasMarketContext && (
                      <Grid
                        item
                        xs={12}
                      >
                        <Alert severity='info'>
                          {t('selectInstanceAndMarketFirst')}
                        </Alert>
                      </Grid>
                    )}

                    {hasMarketContext && !canAssignItemTags && (
                      <Grid
                        item
                        xs={12}
                      >
                        <Alert severity='warning'>
                          {t('noAssignmentPermission')}
                        </Alert>
                      </Grid>
                    )}

                    {!parsedItemTagId && hasMarketContext && canAssignItemTags && selectedItemIds.length > 0 && (
                      <Grid
                        item
                        xs={12}
                      >
                        <Alert severity='info'>
                          {t('assignmentPendingCreateHint')}
                        </Alert>
                      </Grid>
                    )}

                    {!showAssignmentEmptyState && (
                      <>
                        {/* 1. Search — always enabled so users can type ahead */}
                        <Grid
                          item
                          xl={3}
                          md={6}
                          xs={12}
                        >
                          <TextField
                            label={t('search')}
                            placeholder={t('assignmentSearchPlaceholder')}
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                setSearchText(searchInput.trim());
                              }
                            }}
                            fullWidth
                          />
                        </Grid>

                        {/* 2. Catalogue — required before items are shown */}
                        <Grid
                          item
                          xl={3}
                          md={6}
                          xs={12}
                        >
                          <FormControl fullWidth>
                            <InputLabel shrink>{t('filterByCatalogue')}</InputLabel>
                            <Select
                              value={selectedCatalogue}
                              onChange={(event: SelectChangeEvent<string>) => {
                                setSelectedCatalogue(event.target.value);
                                setSelectedCategory('');
                                setSearchInput('');
                                setSearchText('');
                              }}
                              label={t('filterByCatalogue')}
                              displayEmpty
                              notched
                            >
                              <MenuItem value=''>{t('selectCatalogue')}</MenuItem>
                              {catalogues.map((catalogue) => (
                                <MenuItem key={catalogue.id}
                                  value={catalogue.id.toString()}>
                                  {catalogue.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* 3. Category — populated from selected catalogue */}
                        <Grid
                          item
                          xl={3}
                          md={6}
                          xs={12}
                        >
                          <FormControl fullWidth>
                            <InputLabel shrink>{t('filterByCategory')}</InputLabel>
                            <Select
                              value={selectedCategory}
                              onChange={(event: SelectChangeEvent<string>) => setSelectedCategory(event.target.value)}
                              label={t('filterByCategory')}
                              displayEmpty
                              notched
                              disabled={!selectedCatalogue}
                            >
                              <MenuItem value=''>{t('allCategories')}</MenuItem>
                              {categories.map((category) => (
                                <MenuItem key={category.id}
                                  value={category.id.toString()}>
                                  {category.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* 4. Assignment view filter */}
                        <Grid
                          item
                          xl={3}
                          md={6}
                          xs={12}
                        >
                          <FormControl fullWidth>
                            <InputLabel shrink>{t('filterByAssignment')}</InputLabel>
                            <Select
                              value={assignmentView}
                              onChange={(event: SelectChangeEvent<string>) => setAssignmentView(event.target.value as AssignmentView)}
                              label={t('filterByAssignment')}
                              displayEmpty
                              notched
                              disabled={!selectedCatalogue}
                            >
                              <MenuItem value={AssignmentView.All}>{t('allAssignmentStates')}</MenuItem>
                              <MenuItem value={AssignmentView.Assigned}>{t('assignedItemsOnly')}</MenuItem>
                              <MenuItem value={AssignmentView.Unassigned}>{t('unassignedItemsOnly')}</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid
                          item
                          xs={12}
                        >
                          <Grid
                            container
                            spacing={3}
                            justifyContent='flex-end'
                          >
                            <Grid
                              item
                              lg={2}
                              md={3}
                              sm={4}
                              xs={12}
                            >
                              <Button
                                variant='secondary'
                                size='lg'
                                onClick={() => {
                                  setSearchInput('');
                                  setSearchText('');
                                  setSelectedCategory('');
                                  setSelectedCatalogue('');
                                  setAssignmentView(AssignmentView.All);
                                }}
                                fullWidth
                              >
                                {t('clear')}
                              </Button>
                            </Grid>
                            <Grid
                              item
                              lg={2}
                              md={3}
                              sm={4}
                              xs={12}
                            >
                              <Button
                                variant='primary'
                                size='lg'
                                onClick={() => setSearchText(searchInput.trim())}
                                startIcon={<Search />}
                                fullWidth
                                disabled={!selectedCatalogue}
                              >
                                {t('search')}
                              </Button>
                            </Grid>
                          </Grid>
                        </Grid>
                      </>
                    )}

                    {showAssignmentEmptyState ? null : !selectedCatalogue ? (
                      <Grid
                        item
                        xs={12}
                      >
                        <Alert severity='info'>
                          {t('selectCatalogueFirst')}
                        </Alert>
                      </Grid>
                    ) : isLoadingFirstPage ? (
                      <Grid
                        item
                        xs={12}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                          <CircularProgress />
                        </Box>
                      </Grid>
                    ) : displayedItems.length === 0 ? (
                      <Grid
                        item
                        xs={12}
                      >
                        <Box sx={{ py: 6, textAlign: 'center' }}>
                          <Typography
                            variant='textMD'
                            color='text.secondary'
                          >
                            {t('assignmentEmptyState')}
                          </Typography>
                        </Box>
                      </Grid>
                    ) : (
                      <Grid
                        item
                        xs={12}
                      >
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 3,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              px: 3,
                              py: 2,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: { xs: 'flex-start', md: 'center' },
                              gap: 2,
                              flexDirection: { xs: 'column', md: 'row' },
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography
                                variant='textMD'
                                fontWeight={600}
                              >
                                {assignmentHeaderLabel}
                              </Typography>
                            </Box>
                            <Typography
                              variant='textSM'
                              color='text.secondary'
                            >
                              {t('toBeAssignedCount', { count: selectedItemIds.filter((id) => !persistedAssignedItemIdSet.has(id)).length })}
                            </Typography>
                          </Box>

                          {totalItemCount > 0 && (
                            <Typography
                              variant='textXS'
                              color='text.secondary'
                              sx={{ px: 1, pb: 0.5, display: 'block' }}
                            >
                              {t('showingItems', { shown: displayedItems.length, total: totalItemCount })}
                            </Typography>
                          )}
                          <TableVirtuoso
                            data={displayedItems}
                            components={assignmentTableComponents}
                            overscan={20}
                            endReached={loadMoreItems}
                            style={{ height: Math.min(Math.max(displayedItems.length * 56 + 56, 300), 750) }}
                            fixedFooterContent={isLoadingMoreItems ? () => (
                              <TableRow>
                                <TableCell
                                  colSpan={5}
                                  align='center'
                                  sx={{ py: 1.5, borderTop: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}
                                >
                                  <CircularProgress size={20} />
                                </TableCell>
                              </TableRow>
                            ) : null}
                            fixedHeaderContent={() => (
                              <TableRow>
                                <TableCell
                                  padding='checkbox'
                                  sx={{
                                    width: 72,
                                    minWidth: 72,
                                    maxWidth: 72,
                                    pl: 2,
                                    pr: 1,
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 2,
                                    backgroundColor: theme.palette.custom.gray[100],
                                    fontWeight: 600,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                  }}
                                >
                                  <Checkbox
                                    checked={allDisplayedSelected}
                                    indeterminate={!allDisplayedSelected && hasDisplayedSelection}
                                    onChange={(event) => handleSelectAll(event.target.checked)}
                                    sx={{ p: 1 }}
                                    inputProps={{ 'aria-label': t('selectAll') }}
                                  />
                                </TableCell>
                                <TableCell
                                  sx={{
                                    width: '20%',
                                    minWidth: 160,
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 2,
                                    backgroundColor: theme.palette.custom.gray[100],
                                    fontWeight: 600,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                  }}
                                >
                                  {t('sku')}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    minWidth: 280,
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 2,
                                    backgroundColor: theme.palette.custom.gray[100],
                                    fontWeight: 600,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                  }}
                                >
                                  {t('productName')}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    width: '22%',
                                    minWidth: 180,
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 2,
                                    backgroundColor: theme.palette.custom.gray[100],
                                    fontWeight: 600,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                  }}
                                >
                                  {t('category')}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    width: '18%',
                                    minWidth: 140,
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 2,
                                    backgroundColor: theme.palette.custom.gray[100],
                                    fontWeight: 600,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                  }}
                                >
                                  {t('status')}
                                </TableCell>
                              </TableRow>
                            )}
                            itemContent={(_, item) => {
                              const assignmentStatus = getAssignmentStatus(item);

                              return (
                                <>
                                  <TableCell
                                    padding='checkbox'
                                    sx={{
                                      width: 72,
                                      minWidth: 72,
                                      maxWidth: 72,
                                      pl: 2,
                                      pr: 1,
                                    }}>
                                    <Checkbox
                                      checked={selectedItemIdSet.has(item.contentId)}
                                      onChange={() => handleSelectItem(item)}
                                      sx={{ p: 1 }}
                                      inputProps={{ 'aria-label': `${t('selectItem')} ${item.productName}` }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ width: '20%', minWidth: 160, whiteSpace: 'nowrap' }}>{item.itemNumber}</TableCell>
                                  <TableCell sx={{ minWidth: 280 }}>{item.productName}</TableCell>
                                  <TableCell sx={{ width: '22%', minWidth: 180 }}>{item.categoryName}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={assignmentStatus.label}
                                      color={assignmentStatus.color}
                                      size='small'
                                    />
                                  </TableCell>
                                </>
                              );
                            }}
                          />
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Section>
            </Box>
          </Grid>
        </Grid>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 3,
            flexDirection: { xs: 'column', sm: 'row' },
          }}>
          <Button
            variant='secondary'
            size='lg'
            onClick={() => navigate('/item-tags')}
            disabled={isSaving}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {t('cancel')}
          </Button>
          <Button
            variant='primary'
            size='lg'
            onClick={handleSubmit}
            disabled={isSaving || (!canEditBasicInfo && !hasAssignmentChanges)}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {primaryButtonLabel}
          </Button>
        </Box>
      </Stack>

      <MessagesSnackbar
        open={Boolean(feedback)}
        onClose={() => setFeedback(null)}
        message={feedback?.message || ''}
        severity={feedback?.severity || 'success'}
        duration={3000}
      />
    </PageArea>
  );
};

export default ItemTagEditorPage;